import { Euler, Matrix4, Raycaster, Vector3 } from "three";
import { Camera } from "../scenegraph/internal";
import { isDefined } from "../internal";
import { MatrixUtils } from "../utils/internal";
import { Behavior } from "./internal";
/**
 * This Behavior script is designed to be attached to a Three.Camera object and updates its camera matrices according to
 * current zoom and pan values.
 *
 * @class SceneCameraBehaviour
 * @param {SceneGraphNode} parentObject
 * @constructor
 */
export class SceneCameraBehaviour extends Behavior {
    constructor(parentObject, position, orientation) {
        super(parentObject);
        this.positionAbsolute = new Vector3();
        this.zoom = 1.0;
        this.cameraIntrinsics = null;
        this._tMouseVector = null;
        this._positionChanged = false;
        this.position = new Vector3(position[0], position[1], position[2]);
        this.orientation = new Vector3(orientation[0], orientation[1], orientation[2]);
        this.cameraIntrinsics = {
            IMAGE_WIDTH: 4008,
            IMAGE_HEIGHT: 2672,
            PIXEL_SIZE: 0.000009,
            NEAR: 0.0210079,
            FAR: 1200,
            MIN_ZOOM: 1,
            MAX_ZOOM: 3,
        };
        this._tMouseVector = new Vector3();
        if (!(parentObject instanceof Camera)) {
            throw "Wrong Parent Object: SceneCamera Behavior must be attached to a Camera object.";
        }
    }
    setPosition(position) {
        this.position = new Vector3(position[0], position[1], position[2]);
        this._positionChanged = true;
    }
    setOrientation(orientation) {
        this.orientation = new Vector3(orientation[0], orientation[1], orientation[2]);
    }
    getOrientation() {
        return this.orientation;
    }
    setCameraIntrinsics(cameraIntrinsics) {
        if (isDefined(cameraIntrinsics["IMAGE_WIDTH"])) {
            this.cameraIntrinsics["IMAGE_WIDTH"] = cameraIntrinsics["IMAGE_WIDTH"];
        }
        if (isDefined(cameraIntrinsics["IMAGE_HEIGHT"])) {
            this.cameraIntrinsics["IMAGE_HEIGHT"] = cameraIntrinsics["IMAGE_HEIGHT"];
        }
        if (isDefined(cameraIntrinsics["PIXEL_SIZE"])) {
            this.cameraIntrinsics["PIXEL_SIZE"] = cameraIntrinsics["PIXEL_SIZE"];
        }
        if (isDefined(cameraIntrinsics["NEAR"])) {
            this.cameraIntrinsics["NEAR"] = cameraIntrinsics["NEAR"];
        }
        if (isDefined(cameraIntrinsics["FAR"])) {
            this.cameraIntrinsics["FAR"] = cameraIntrinsics["FAR"];
        }
        if (isDefined(cameraIntrinsics["MIN_ZOOM"])) {
            this.cameraIntrinsics["MIN_ZOOM"] = cameraIntrinsics["MIN_ZOOM"];
        }
        if (isDefined(cameraIntrinsics["MAX_ZOOM"])) {
            this.cameraIntrinsics["MAX_ZOOM"] = cameraIntrinsics["MAX_ZOOM"];
        }
    }
    /**
     * Will be called in every render pass
     *
     * reads:   traversalstate.zoom
     *          traversalstate.panX
     *          traversalstate.panY
     *
     * writes:  traversalstate.matprojection
     *          traversalstate.matmodel
     *          traversalstate.matmodelview
     *          traversalstate.raycaster
     *          traversalstate.cameraIntrinsics
     *          traversalstate.sceneCamPosChanged
     *          traversalstate.sceneCamPos
     *
     * @method render
     * @param {TraversalState} traversalState
     */
    render(traversalState) {
        if (this.disable)
            return;
        if (this._positionChanged) {
            traversalState.sceneCamPosChanged = true;
            this._positionChanged = false;
        }
        // Set up View Matrix
        const matview = new Matrix4()
            .makeRotationFromEuler(new Euler((this.orientation.x * Math.PI) / 180, (this.orientation.y * Math.PI) / 180, (this.orientation.z * Math.PI) / 180, "XYZ"))
            .transpose();
        this.zoom = traversalState.zoom;
        // Set up Projection Matrix
        const q = MatrixUtils.ZoomMatrix(traversalState.scene.engine.getContextWidth(), traversalState.scene.engine.getContextHeight(), this.cameraIntrinsics.IMAGE_WIDTH, this.cameraIntrinsics.IMAGE_HEIGHT, this.cameraIntrinsics.MAX_ZOOM, traversalState.zoom, traversalState.panX, traversalState.panY);
        q.invert();
        traversalState.matZoom = q.clone();
        const p = MatrixUtils.ProjectionFromCamera(this.cameraIntrinsics.IMAGE_WIDTH, this.cameraIntrinsics.IMAGE_HEIGHT, this.cameraIntrinsics.PIXEL_SIZE, this.cameraIntrinsics.NEAR, this.cameraIntrinsics.FAR);
        const matprojection = q.multiply(p);
        traversalState.matProjection = matprojection;
        this.parentObject.iCamera.projectionMatrix = matprojection;
        //set up Model Matrix
        const matmodel = new Matrix4();
        matmodel.elements[12] = -(this.position.x - traversalState.scene.engine.centroid[0]);
        matmodel.elements[13] = -(this.position.y - traversalState.scene.engine.centroid[1]);
        matmodel.elements[14] = -(this.position.z - traversalState.scene.engine.centroid[2]);
        traversalState.matModel = matmodel;
        traversalState.matModelView = matview.clone().multiply(matmodel);
        const matmvp = new Matrix4();
        traversalState.matMVP = matmvp.multiplyMatrices(traversalState.matProjection, traversalState.matModelView);
        traversalState.cameraIntrinsics = this.cameraIntrinsics;
        this.positionAbsolute.set(-matmodel.elements[12], -matmodel.elements[13], -matmodel.elements[14]);
        //update the raycaster for picking
        this._tMouseVector.x =
            2 * (traversalState.actualMousePos[0] / traversalState.scene.engine.getContextWidth()) - 1;
        this._tMouseVector.y =
            1 - 2 * (traversalState.actualMousePos[1] / traversalState.scene.engine.getContextHeight());
        // set two vectors with opposing z values
        this._tMouseVector.z = -1.0;
        let vector = this._tMouseVector.clone();
        const camera = traversalState.cameras["sceneCamera"].iCamera;
        let end = new Vector3(vector.x, vector.y, 1.0);
        vector = vector.unproject(camera);
        end = end.unproject(camera);
        // find direction from vector to end
        end.sub(vector).normalize();
        traversalState.rayCaster = new Raycaster(vector, end);
        //distance in welcher Punkte selektiert werden
        traversalState.rayCaster.params.Points.threshold = (1.0 / this.zoom) * 2;
        traversalState.sceneCamPos = this.position;
    }
    getLatLon() {
        return [0, 0, this.parentObject.iCamera.fov];
    }
}
//# sourceMappingURL=scenecamerabehavior.js.map